
import { getReportFile, getReportList, generateReport } from '@/api';
import DateSelect from '@/components/dashBoard/selectsFields/components/DateSelect/DateSelect.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import { format, utcToZonedTime } from 'date-fns-tz';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Header, User } from '@/models/main';
import { ReportRequestParams, ReportResponse } from '@/models/reports';
import { notificationErrorText, timeZoneDefault } from '@/utils/PortalConstants';
import { blobDownloader } from '@/utils/Common';
import * as Sentry from '@sentry/vue';
import { formatDateStringWithTZ } from '@/utils/InputProcessingUtilities';
import { mapState } from 'vuex';

@Component({
  components: {
    BackButton,
    DateSelect,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class DriverReport extends Vue {
  private isLoading = false;
  private isDownloadLoading = false;
  private dates: string[] = [];
  private driverReport: ReportResponse[] = [];
  private generatePeriod = false;
  private user: User;
  private formatDateStringWithTZ = formatDateStringWithTZ;

  private headers: Header[] = [
    { text: 'Id', value: 'id', align: 'left' },
    { text: 'Title', value: 'title', align: 'left', sortable: false },
    { text: 'Created by', value: 'created_by', align: 'left' },
    { text: 'Created at', value: 'created_at', align: 'left' },
    { text: 'Approved at', value: 'approved_at', align: 'left' },
    { text: 'Download', value: 'link', align: 'left', sortable: false },
  ];

  // Date lookup
  private changeDate(dates: string[]): void {
    this.dates = dates;
  }
  // Driver report
  private getDriverReportList() {
    this.isLoading = true;
    const params: ReportRequestParams = {
      created_at: this.dates,
      report_type: 1,
    };
    getReportList(params)
      .then((res: ReportResponse[]) => {
        this.driverReport = res;
      })
      .catch(() => {
        this.$notify({
          group: 'network',
          title: 'Error getting Driver report',
          text: notificationErrorText,
          type: 'warn',
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isLoading = false;
      });
  }

  private getGenerateParams() {
    let params: ReportRequestParams;
    if (this.generatePeriod) {
      params = {
        month: this.dates,
        report_type: 1,
      };
    } else {
      params = {
        week: this.dates,
        report_type: 1,
      };
    }
    return params;
  }

  /**
   * Generate a report on backend
   * Type = 1 for Driver report type
   * @private
   */
  private generateDriverReport() {
    // download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isLoading = true;
    this.isDownloadLoading = true;
    const params = this.getGenerateParams();
    generateReport(params)
      .then(() => {
        this.$notify({
          group: 'network',
          title: 'Driver report requested',
          text: 'Wait for the result. It could take up to 10-15m in case of big Report.',
          type: 'success',
        });
        this.getDriverReportList();
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error generation Driver report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isDownloadLoading = false;
      });
  }

  /**
   * Download a report file by report ID
   * @param report_id
   * @private
   */
  private downloadDriverReport(report_id: number) {
    // TODO: download loader should be used, but I don't want to spent time on template to implement it
    // so using isLoading for now
    this.isLoading = true;
    this.isDownloadLoading = true;
    getReportFile(report_id)
      .then(res => {
        blobDownloader(res);
      })
      .catch(err => {
        this.$notify({
          group: 'network',
          title: 'Error getting Driver report',
          text: notificationErrorText,
          type: 'warn',
        });
        Sentry.withScope(scope => {
          scope.setLevel('error');
          Sentry.captureException(err);
        });
      })
      .finally(() => {
        this.isLoading = false;
        this.isDownloadLoading = false;
      });
  }

  private mounted(): void {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.dates = [format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd')];
  }
}
